import React, {useEffect, useRef, useState} from "react";
import {HiCalendar, HiChip, HiCog, HiBell, HiPuzzle, HiUserCircle} from 'react-icons/hi';
import {FaRandom} from "react-icons/fa";
import {useJwt} from "../../helpers/common";
import {SettingType} from "./types";
import {useDispatch, useSelector} from "react-redux";
import {setActiveTab} from "../../redux/slices/settings";
import Integrations from "./Integrations/Integrations";
import VectorSettings from "./VectorSettings/VectorSettings";
import UserPreferencesSection from "./VectorSettings/UserPreferencesSection";
import OrgSkills from "./OrgSkills/OrgSkills";
import Schedules from "./Schedules/Schedules";
import Notifications from "./Notifications/Notifications";
import OrgSettings from "./OrgSettings/OrgSettings";
import {useSideNavContext} from "../../context/SideNavContext";
import {useLocation, useNavigate} from "react-router-dom";
import {HiBuildingOffice} from "react-icons/hi2";
import {useOrganizationService} from "../../services";
import { SettingsSidenavGuide } from "../../features/OnboardingGuide/SettingsSidenavGuide/SettingsSidenavGuide";

const Setting = () => {
  const jwtData: any = useJwt();
  const dispatch = useDispatch();
  const { isOpenOnSmallScreens } = useSideNavContext();
  const { pathname, hash } = useLocation();
  const { fetchOrganizationSkill } = useOrganizationService();
  const navigate = useNavigate();

  const [isNavVisible, setIsNavVisible] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [resolvedRole, setResolvedRole] = useState<string | null>(null);
  const isRoleLoading = resolvedRole === null;

  const {
    activeTab
  } = useSelector((state: any) => state.settings);

  useEffect(() => {
    if (jwtData && typeof jwtData.org_role === "string") {
      setResolvedRole(jwtData.org_role);
    }
  }, [jwtData]);

  useEffect(() => {
    if(jwtData?.org_id) {
      fetchOrganizationSkill(jwtData?.org_id);
    }
  }, [jwtData?.org_id]);

  useEffect(() => {
    if(resolvedRole !== 'admin') {
      dispatch(setActiveTab(SettingType.PERSONALIZATION));
    }
  }, []);

  useEffect(() => {
    const validTabs: Set<string> = new Set(Object.values(SettingType));
    const normalized = hash && hash.startsWith('#') ? hash.slice(1) : hash;
    if (normalized && validTabs.has(normalized)) {
      dispatch(setActiveTab(normalized as SettingType));
    }
  }, [dispatch, pathname, hash]);

  const handleTabChange = (tab: SettingType) => {
    dispatch(setActiveTab(tab));
    navigate({ pathname: '/settings', hash: `#${tab}` });
    setIsDropdownOpen(false);
  }


  const integrationSettingLinkRef = useRef<any>(null);
  const skillsSettingLinkRef = useRef<any>(null);
  const holidaySettingLinkRef = useRef<any>(null);


  if (isRoleLoading) {
    return (
      <div className="flex-1" style={{ width: !isOpenOnSmallScreens ? 'calc(100vw - 255px)' : 'calc(100vw - 65px)', height: '100vh' }}>
        <div className="flex items-center justify-center h-full bg-secondary-light dark:bg-secondary-dark">
          <div className="w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-gray-700 dark:border-t-white rounded-full animate-spin" />
        </div>
      </div>
    );
  }


  return (
    <>
      <div
        className="flex-1"
        style={{
          width: !isOpenOnSmallScreens
            ? "calc(100vw - 255px)"
            : "calc(100vw - 65px)",
          height: "100vh",
        }}
      >
        {/* Settings button with dropdown */}
        <div className="lg:hidden fixed top-4 right-4 z-50">
          <div className="relative">
            <button
              onClick={() => {
                setIsDropdownOpen(!isDropdownOpen);
                setIsNavVisible(true);
              }}
              className="flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800"
              aria-label="Settings menu"
            >
              <HiCog className="w-5 h-5 hover:rotate-90 transition-transform duration-300" />
            </button>

            {/* Dropdown menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleTabChange(SettingType.INTEGRATION)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.INTEGRATION
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <HiPuzzle className="w-4 h-4 shrink-0" />
                  <span>Integrations</span>
                </button>
                <button
                  onClick={() => handleTabChange(SettingType.LLM)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.LLM
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <HiChip className="w-4 h-4 shrink-0" />
                  <span>LLM Settings</span>
                </button>
                <button
                  onClick={() => handleTabChange(SettingType.ORG_SETTINGS)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.ORG_SETTINGS
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <HiBuildingOffice className="w-4 h-4 shrink-0" />
                  <span>Org Settings</span>
                </button>
                <button
                  onClick={() => handleTabChange(SettingType.SKILLS)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.SKILLS
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <FaRandom className="w-4 h-4 shrink-0" />
                  <span>Skills</span>
                </button>
                <button
                  onClick={() => handleTabChange(SettingType.HOLIDAYS)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.HOLIDAYS
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <HiCalendar className="w-4 h-4 shrink-0" />
                  <span>Holiday List</span>
                </button>

                <button
                  onClick={() => handleTabChange(SettingType.NOTIFICATIONS)}
                  className={`
                  w-full flex items-center gap-2 px-4 py-2.5 text-sm
                  ${
                    activeTab === SettingType.NOTIFICATIONS
                      ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  }
                `}
                >
                  <HiBell className="w-4 h-4 shrink-0" />
                  <span>Notifications</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 w-full h-full">
          <div className="flex-1 h-full w-full">
            <div className="lg:hidden flex max-h-[68px] items-center justify-between px-6 py-4 rounded-br-xl rounded-bl-xl bg-tertiary-light dark:bg-tertiary-dark">
              <div className="flex-1 flex items-center justify-center">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white tracking-tight font-['Inter']">
                  Settings
                </h1>
              </div>
            </div>

            {/* Settings Content */}
            <div
              className="bg-secondary-light dark:bg-secondary-dark shadow-sm h-full"
              style={{ height: "calc(100vh)" }}
            >
              <div className="flex h-full lg:flex-row flex-col">
                <div
                  className={`
                lg:w-64 w-full lg:p-4 p-2 lg:border-r lg:border-b-0 border-b border-gray-100 dark:border-gray-700 lg:h-full transition-all duration-300 
                ${isNavVisible ? "block" : "hidden lg:block"}
              `}
                >
                  <div className="lg:block hidden mb-10">
                    <h1 className="text-3xl font-semibold text-gray-800 dark:text-white tracking-tight font-['Inter']">
                      Settings
                    </h1>
                  </div>
                  {/* Desktop Navigation */}
                  <div className="hidden lg:block">
                    {resolvedRole === "admin" && (
                      <>
                        <div className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wider mb-4">
                          Product Settings
                        </div>
                        <div className="flex flex-col space-y-1">
                          <button
                            ref={integrationSettingLinkRef}
                            onClick={() =>
                              handleTabChange(SettingType.INTEGRATION)
                            }
                            className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                          ${
                            activeTab === SettingType.INTEGRATION
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                          }
                        `}
                          >
                            <HiPuzzle className="w-5 h-5 shrink-0" />
                            <span className="truncate">Integrations</span>
                          </button>
                          <button
                            onClick={() => handleTabChange(SettingType.LLM)}
                            className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                          ${
                            activeTab === SettingType.LLM
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                          }
                        `}
                          >
                            <HiChip className="w-5 h-5 shrink-0" />
                            <span className="truncate">LLM Settings</span>
                          </button>
                        </div>
                      </>
                    )}

                    {resolvedRole === "admin" && (
                      <>
                        <div className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wider mt-8 mb-4">
                          Organization Settings
                        </div>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() =>
                              handleTabChange(SettingType.ORG_SETTINGS)
                            }
                            className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                          ${
                            activeTab === SettingType.ORG_SETTINGS
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                          }
                        `}
                          >
                            <HiBuildingOffice className="w-5 h-5 shrink-0" />
                            <span className="truncate">Org Settings</span>
                          </button>
                          <button
                            ref={skillsSettingLinkRef}
                            onClick={() => handleTabChange(SettingType.SKILLS)}
                            className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors font-['Inter'] 
                          ${
                            activeTab === SettingType.SKILLS
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                          }
                        `}
                          >
                            <FaRandom className="w-5 h-5 shrink-0" />
                            <span className="truncate">Org Skills</span>
                          </button>

                          <button
                            onClick={() =>
                              handleTabChange(SettingType.HOLIDAYS)
                            }
                            ref={holidaySettingLinkRef}
                            className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                          ${
                            activeTab === SettingType.HOLIDAYS
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                              : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                          }
                        `}
                          >
                            <HiCalendar className="w-5 h-5 shrink-0" />
                            <span className="truncate">Holidays</span>
                          </button>

                          <button
                            onClick={() =>
                              handleTabChange(SettingType.NOTIFICATIONS)
                            }
                            className={`
                            flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                            ${
                              activeTab === SettingType.NOTIFICATIONS
                                ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                                : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                            }
                          `}
                          >
                            <HiBell className="w-5 h-5 shrink-0" />
                            <span className="truncate">Notifications</span>
                          </button>
                        </div>
                      </>
                    )}

                    <div className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wider mt-8 mb-4">
                      User Settings
                    </div>
                    <div className="flex flex-col space-y-1">
                      <button
                        onClick={() =>
                          handleTabChange(SettingType.PERSONALIZATION)
                        }
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base transition-colors 
                      ${
                        activeTab === SettingType.PERSONALIZATION
                          ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                          : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800"
                      }
                    `}
                      >
                        <HiUserCircle className="w-5 h-5 shrink-0" />
                        <span className="truncate">Personalization</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Settings Content Area */}
                <div className="flex-1 h-full overflow-y-auto bg-light-surfacePrimary dark:bg-tertiary-dark">
                  {activeTab === SettingType.INTEGRATION &&
                    resolvedRole === "admin" && <Integrations />}
                  {activeTab === SettingType.LLM &&
                    resolvedRole === "admin" && <VectorSettings />}
                  {activeTab === SettingType.PERSONALIZATION && (
                    <UserPreferencesSection />
                  )}
                  {activeTab === SettingType.SKILLS &&
                    resolvedRole === "admin" && <OrgSkills />}
                  {activeTab === SettingType.ORG_SETTINGS &&
                    resolvedRole === "admin" && <OrgSettings />}
                  {activeTab === SettingType.HOLIDAYS &&
                    resolvedRole === "admin" && <Schedules />}
                  {activeTab === SettingType.NOTIFICATIONS &&
                    resolvedRole === "admin" && <Notifications />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SettingsSidenavGuide
        refs={{
          integrationSettingLinkRef,
          skillsSettingLinkRef,
          holidaySettingLinkRef,
        }}
      />
    </>
  );
};

export default Setting;